import {Injectable} from "@angular/core";
import {HttpService} from "./utils/http.service";
import {GlobalsService} from "./utils/globals.service";
import {AppointmentModel, IEmailTemplateModel, IEmailModel, IFinalServiceTemplateCustomizationModel, IEstimateTemplateCustomizationModel, IPaymentTemplateCustomizationModel, IBatchTemplateCustomizationModel} from "@models";

// The accent colors offered on the Payment Template settings page. Mirrors Models/PaymentTemplateTheme.cs
// Palette — keep the two in step so the picker can't offer a color the email won't render.
export const PAYMENT_THEME_COLORS: { name: string, hex: string }[] = [
    { name: "Cobalt Blue", hex: "002BC7" },
    { name: "Sky Blue", hex: "1565C0" },
    { name: "Navy Blue", hex: "012F5F" },
    // Sampled from a dealer's site banner. Bright enough that the white band text and the
    // theme-colored labels on white both land near 1.5:1 contrast; the old 00897B was 4.3:1.
    { name: "Teal", hex: "25EBF9" },
    { name: "Forest Green", hex: "166534" },
    { name: "Light Purple", hex: "A855F7" },
    { name: "Dark Purple", hex: "4C1D95" },
    { name: "Dark Brown", hex: "5A3E36" },
    { name: "Gold", hex: "C2A878" },
    { name: "Taupe", hex: "8B7D6B" },
    { name: "Charcoal Black", hex: "222222" },
    { name: "Slate Blue-Gray", hex: "475569" },
    { name: "Maroon Red", hex: "8B1538" },
];

export const PAYMENT_THEME_DEFAULT_COLOR = "002BC7";

// The look shared by all four accounting templates. Mirrors Models/TemplateThemeModel.cs.
export interface ISendTemplateSampleResult {
    sent: boolean;
    sentTo: string;
    message: string;
}

export interface ITemplateThemeModel {
    themeColor: string;
    // Only the invoice / sales order and payment receipt layouts have a colored header band to flip.
    useLightHeader: boolean;
}

@Injectable({
    providedIn: 'root'
})
export class EmailService {
    // Stand-in for a company with no logo, so a preview never shows a broken image.
    private static readonly LOGO_PLACEHOLDER = "data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='90' height='40'><rect width='90' height='40' rx='4' fill='%23dddddd'/></svg>";

    // Every user is handed a random stock cartoon (docs.skedit.com/avatars/Avatar-NN.png) on creation, so
    // profilePictureUrl is almost never blank. Only a photo the user actually uploaded is shown; for anyone
    // else the <img> is dropped altogether, which is exactly what the sent email does
    // (UserModel.HasUploadedProfilePicture on the server). No placeholder, so the preview and the email agree.
    private static readonly STOCK_AVATAR_URL_PREFIX = "https://docs.skedit.com/avatars/Avatar-";
    // dropCell mirrors EmailService.GenerateInvoiceEmail for the technician photo: a cell holding nothing
    // but the photo goes with it, so its empty column doesn't indent the details beside it.
    private static applyProfilePicture(html: string, token: string, profilePictureUrl: string, dropCell = false): string {
        const hasUploadedPhoto = !!profilePictureUrl
            && !profilePictureUrl.toLowerCase().startsWith(EmailService.STOCK_AVATAR_URL_PREFIX.toLowerCase());

        if (hasUploadedPhoto)
            return html.split(token).join(profilePictureUrl);

        if (dropCell)
            html = html.replace(new RegExp(`<td[^>]*>\\s*<img[^>]*?${token}[^>]*?>\\s*</td>`, "g"), "");

        return html.replace(new RegExp(`<img[^>]*?${token}[^>]*?>`, "g"), "");
    }

    // Stand-in invoice behind the Final Service Template preview.
    private static readonly SAMPLE_LINE_ITEMS = [
        { sku: "PMP-1100", description: "Variable speed pump", quantity: 1, price: 899.00 },
        { sku: "FLT-220", description: "Cartridge filter element", quantity: 2, price: 74.50 },
        { sku: "LBR", description: "Labor - installation", quantity: 3, price: 85.00 },
    ];
    private static readonly SAMPLE_TAX_RATE = 0.0675;

    constructor(private readonly httpService: HttpService) {
    }

    async getEmailTemplates(): Promise<IEmailTemplateModel[]> {
        return await this.httpService.get('/emailTemplates/getEmailTemplates');
    }
    async updateEmailTemplate(model: IEmailTemplateModel): Promise<IEmailTemplateModel> {
        return await this.httpService.post('/emailTemplates/updateEmailTemplate', model);
    }
    async getAllEmails(
        skip: number = 0,
        take: number = 200,
        search: string = '',
        customerId?: number
    ): Promise<IEmailModel[]> {
        const params = new URLSearchParams({
            skip: String(skip),
            take: String(take)
        });

        if (search) {
            params.set('search', search);
        }

        if (customerId !== null && customerId !== undefined) {
            params.set('customerId', String(customerId));
        }

        return await this.httpService.get(
            `/emailstrack/getAllEmails?${params}`
        );
    }
    async getEmailFromInvoiceId(invoiceId: number | string): Promise<IEmailModel[]> {
        if (invoiceId === null || invoiceId === undefined || String(invoiceId).trim() === '') {
            return [];
        }
        const encodedId = encodeURIComponent(String(invoiceId));
        const url = `/emailstrack/getEmailsFromInvoiceId?invoiceId=${encodedId}`;
        return await this.httpService.get(url);
    }
    async updateFinalServiceTemplateCustomization(model: IFinalServiceTemplateCustomizationModel): Promise<IFinalServiceTemplateCustomizationModel> {
        return await this.httpService.post('emailTemplates/updateFinalServiceTemplateCustomization', model)
    }

    async getFinalServiceTemplateCustomization(): Promise<IFinalServiceTemplateCustomizationModel> {
        return await this.httpService.get('emailTemplates/getFinalServiceTemplateCustomization')
    }

    // Renders the PDF a customer receives alongside this template, using sample line items rather than a
    // real invoice. Returns a URL to open. docType: estimate | salesorder | invoice | receipt.
    // forPrint: the black-and-white print version, even before Print in black and white is saved.
    async generateTemplatePreviewPdf(docType: string, forPrint: boolean = false): Promise<string> {
        return await this.httpService.get(`/invoices/generateTemplatePreviewPdf?docType=${docType}&forPrint=${forPrint}`);
    }

    // Returns the saved logo URL, built by the API for the environment it is running in. The caller
    // shows that URL rather than assembling one of its own - a guessed docs host pointed staging and RC
    // at production, where the file that was just saved does not exist.
    // Sends the email (or text) this template produces, filled in with sample data, to the signed-in
    // user. The recipient comes from the token on the API side, not from here.
    async sendTemplateSample(docType: string, channel: string): Promise<ISendTemplateSampleResult> {
        return await this.httpService.post('emailTemplates/sendTemplateSample', { docType, channel });
    }

    async updateFinalServiceTemplateLogo(enc64Image: string): Promise<string> {
        const result = await this.httpService.post("/emailTemplates/updateFinalServiceTemplateLogo", JSON.stringify(enc64Image));
        return result?.url;
    }

    // Live-preview render for the Final Service Template editor — same treatment as the estimate
    // preview: real company/logo info, sample data everywhere the email would pull from an actual
    // invoice, so nothing shows up as a raw %TOKEN%.
    // headerText comes from the TinyMCE instance (live) rather than the model, which only updates on save.
    getReplacedFinalServiceEmailHTML(model: IFinalServiceTemplateCustomizationModel, headerText: string) {
        let html = this.getBaseInvoiceEmailRawHTML();

        // Theme + header band. Same palette as the payment receipt, so a company's emails read as one set.
        const themeColor = this.normalizeThemeColor(model?.themeColor);
        html = html.split("%THEME_COLOR%").join(themeColor);
        html = html.split("%THEME_TINT%").join(this.tintThemeColor(themeColor, .12));

        // Header band only. Mirrors InvoiceServices: the light option swaps the solid band for white with
        // accent-colored text, so a dark logo stays readable. Everything below still uses %THEME_COLOR%.
        html = html.split("%HEADER_BG%").join(model?.useLightHeader ? "FFFFFF" : themeColor);
        html = html.split("%HEADER_TEXT%").join(model?.useLightHeader ? themeColor : "FFFFFF");

        html = html.split("%SERVICE_HEADER_TITLE%").join(this.escapeHtml(model?.headerTitle || "Service Record"));
        html = html.split("%SERVICE_HEADER_SUBTITLE%").join(this.escapeHtml(model?.headerSubtitle));

        // Model-driven (real) fields
        html = html.replace("%CUSTOM_HEADER_TEXT%", headerText || "");
        // Escaped, not raw — InvoiceServices does the same to the custom notes, so markup typed in
        // that box shows as text in the email and has to show as text here too.
        html = html.replace("%CUSTOM_NOTES%", this.escapeHtml(model?.customNotes).replace(/\r\n|\n/g, "<br>"));
        html = html.replace("%REVIEW_BUTTON%", this.buildReviewButtonHtml(model?.reviewUrl));

        const company = GlobalsService.company;
        const user = GlobalsService.userInfo;

        // Same strip EmailService.GenerateInvoiceEmail does when the template hides the technician, and it
        // has to happen here too or the preview would keep showing a block the real email leaves out. The
        // markers are the ones the server matches on; everything between them goes.
        if (model?.hideTechnicianSection)
            html = html.replace(/<!-- Technician Info -->[\s\S]*?<!-- Service Location -->/i, "<!-- Service Location -->");

        // The technician is whoever completed the appointment; the logged-in user stands in for them.
        html = EmailService.applyProfilePicture(html, "%TECHNICIAN_IMAGE_URL%", user?.profilePictureUrl, true);

        // Mirrors InvoiceServices.PrepareInvoiceEmail. The Header Image only fills the band's own slot
        // (%HEADER_IMAGE%), falling back to the company logo when none is uploaded. The footer sits on
        // white and keeps %COMPANY_LOGO%, so it always shows the company logo: a white-on-transparent
        // logo uploaded for the colored band would disappear there.
        const companyLogo = company?.logoUrl || EmailService.LOGO_PLACEHOLDER;
        html = html.split("%HEADER_IMAGE%").join(model?.headerImageUrl || companyLogo);
        html = html.split("%COMPANY_LOGO%").join(companyLogo);

        const completedDate = new Date();
        const dueDate = new Date(completedDate.getFullYear(), completedDate.getMonth(), completedDate.getDate() + 30);

        const companyAddress = [company?.address1, [company?.city, company?.state].filter(x => x).join(", "), company?.zipcode]
            .filter(x => x)
            .join(" ");

        const totals = this.sampleInvoiceTotals();

        const values: { [key: string]: string } = {
            // Real company info (pulled live from the logged-in company)
            "%COMPANY_NAME%": company?.companyName || "",
            "%COMPANY_FULL_ADDRESS%": companyAddress,
            "%COMPANY_ADDRESS1%": company?.address1 || "",
            "%COMPANY_CITY%": company?.city || "",
            "%COMPANY_STATE%": company?.state || "",
            "%COMPANY_POSTAL_CODE%": company?.zipcode || "",
            "%COMPANY_PHONE_NUMBER%": company?.phoneNumber || "",
            "%COMPANY_EMAIL%": company?.email || "",
            // Website isn't carried on the Angular company model, so it stays a sample value.
            "%COMPANY_WEBSITE%": "www.example.com",
            // %TECHNICIAN_IMAGE_URL% is handled above: the <img> is dropped unless the user has a real photo.
            "%TECHNICIAN_NAME%": user?.fullName || "Sample Technician",
            "%TECHNICIAN_NOTES%": "Replaced the pump seal and rebalanced the water chemistry.",
            "%DATE_COMPLETED%": completedDate.toLocaleDateString(),
            // Customer + invoice fields have no real source on the settings page, so these stay as
            // sample placeholders. When the email actually sends, they're filled from the invoice/customer.
            // The header text is free-form, so anything the user can insert from the merge-field list
            // needs a value here or it renders as a raw %TOKEN% in the preview.
            "%CUSTOMER_FULL_NAME%": "Sample Customer",
            "%CUSTOMER_DISPLAY_NAME%": "Sample Customer",
            "%CUSTOMER_FIRST_NAME%": "Sample",
            "%CUSTOMER_LAST_NAME%": "Customer",
            "%CUSTOMER_COMPANY_NAME%": "Sample Customer LLC",
            "%CUSTOMER_FULL_ADDRESS%": "123 Main St, Anytown, ST 00000",
            "%CUSTOMER_ADDRESS1%": "123 Main St",
            "%CUSTOMER_ADDRESS2%": "",
            "%CUSTOMER_CITY%": "Anytown",
            "%CUSTOMER_STATE%": "ST",
            "%CUSTOMER_ZIP_CODE%": "00000",
            "%CUSTOMER_POSTAL_CODE%": "00000",
            "%CUSTOMER_COUNTRY%": "USA",
            "%CUSTOMER_HOME_PHONE%": "(555) 555-1234",
            "%CUSTOMER_CELL_PHONE%": "(555) 555-5678",
            "%CUSTOMER_WORK_PHONE%": "(555) 555-9012",
            "%CUSTOMER_EMAIL%": "customer@example.com",
            "%CUSTOMER_PAYMENT_TERMS%": "Net 30",
            "%INVOICE_TYPE%": "Invoice",
            "%QUOTE_OR_INVOICE_TEXT%": "Invoice",
            // Count-agnostic lower-case form, filled from the real document type when the email sends.
            "%DOCUMENT_TYPE%": "invoice(s)",
            "%INVOICE_NUMBER%": "0000",
            "%INVOICE_NUMBERS%": "0000",
            "%PO_NUMBER%": "N/A",
            "%INVOICE_DATE%": completedDate.toLocaleDateString(),
            "%INVOICE_DUE_DATE%": dueDate.toLocaleDateString(),
            "%ONLINE_PAYMENT_LINK%": "#",
            // No currency symbol — the template already prints a literal $ in front of this one.
            "%BALANCE_DUE%": totals.total.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
            "%LINE_ITEMS%": this.buildSampleLineItemsHtml(),
            "%PHOTO_IMAGES%": this.buildSamplePhotosHtml(),
            // Orenda history only renders for Orenda companies, and the accept/pay buttons collapse
            // to the single merged button — same as InvoiceServices does when it builds the real email.
            "%ORENDA_HISTORY%": "",
            "%ACCEPT_BUTTON%": this.buildSampleAcceptButtonHtml(),
            "%PAY_BUTTON%": "",
        };
        for (const key of Object.keys(values)) {
            html = html.split(key).join(values[key]);
        }
        return html;
    }

    // Mirrors the buttons InvoiceServices.PrepareInvoiceEmail drops into %ACCEPT_BUTTON%. The sample is an
    // invoice, and an invoice never carries an accept button (accepting is a quoting step), so the only
    // button it can show is PAY NOW, and only when online payments are on.
    private buildSampleAcceptButtonHtml(): string {
        if (!GlobalsService.company?.useOnlinePayments)
            return "";

        return `<table align="center" cellpadding="0" cellspacing="0" border="0"><tr>
  <td align="center" style="padding:5px;">
    <a href="#" style="display:inline-block;background:#00ff00;color:#000000;font-family:Arial,sans-serif;font-size:13px;font-weight:bold;line-height:120%;text-decoration:none;padding:12px 30px;border-radius:6px;">PAY NOW</a>
  </td>
</tr></table>`;
    }

    // Mirrors InvoiceServices' review-button block: no review link configured means no button at all.
    private buildReviewButtonHtml(reviewUrl: string): string {
        if (!reviewUrl || !reviewUrl.trim())
            return "";

        return `<div style="font-family:Arial, sans-serif;font-size:14px;font-weight:bold;line-height:1.5;text-align:center;color:#333333;margin-top:5px;">
<a href="${reviewUrl}" target="_blank" rel="noopener noreferrer"
   style="display:inline-block;background:#00ff00;color:#000000;font-family:Arial,sans-serif;font-size:13px;font-weight:bold;line-height:120%;text-decoration:none;padding:12px 30px;border-radius:6px;margin-right:10px;">
   LEAVE US A REVIEW
</a>
</div>`;
    }

    // Stand-in for the job photo carousel EmailService.BuildJobPhotosCarousel builds from the selected
    // job photos. The real email drops the whole "Photos from the Job" band when there are none, but the
    // preview always shows it so the user can see what that section looks like.
    private buildSamplePhotosHtml(): string {
        const photo = "data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='150' height='110'><rect width='150' height='110' rx='6' fill='%23e6e6e6'/></svg>";

        let cells = "";
        for (let i = 0; i < 3; i++)
            cells += `<td style="text-align:center;"><img src="${photo}" alt="" style="max-width:150px;border-radius:6px;display:block;margin:0 auto;" /></td>`;

        return `<div style="margin:0 auto;max-width:500px;"><table align="center" border="0" cellpadding="5" cellspacing="0" role="presentation" style="width:100%;text-align:center;"><tr>${cells}</tr></table></div>`;
    }

    // One source of truth for the preview's numbers, so the balance in the blue band always agrees with
    // the Amount Due at the foot of the sample invoice table.
    private sampleInvoiceTotals(): { itemSubtotal: number, tax: number, total: number } {
        const itemSubtotal = EmailService.SAMPLE_LINE_ITEMS.reduce((sum, x) => sum + (x.quantity * x.price), 0);
        const tax = itemSubtotal * EmailService.SAMPLE_TAX_RATE;

        return { itemSubtotal, tax, total: itemSubtotal + tax };
    }

    // Sample invoice table — same markup, classes and column rules as EmailService.BuildInvoiceLineItemsHtml,
    // including the company's Hide Quantity / Hide Price settings, so the preview matches what sends.
    private buildSampleLineItemsHtml(): string {
        const hideQty = GlobalsService.company?.hideInvoiceQuantity === true;
        const hidePrice = GlobalsService.company?.hideInvoicePrice === true;

        const items = EmailService.SAMPLE_LINE_ITEMS;
        const totals = this.sampleInvoiceTotals();

        const money = (value: number) => value.toLocaleString(undefined, { style: "currency", currency: "USD" });

        const itemRow = (item: { sku: string, description: string, quantity: number, price: number }) => {
            let row = `<tr><td class="invoice-td sku" style="font-size:12px; padding:8px 2px;">${item.sku}</td>`;
            row += `<td class="invoice-td description" style="font-size:12px; padding:8px 2px;">${item.description}</td>`;
            if (!hideQty)
                row += `<td class="invoice-td qty" style="font-size:12px; padding:8px 2px; text-align:right;">${item.quantity}</td>`;
            if (!hidePrice)
                row += `<td class="invoice-td price" style="font-size:12px; padding:8px 2px; text-align:right;">${money(item.price)}</td>`;
            row += `<td class="invoice-td total" style="font-size:12px; padding:8px 2px; text-align:right;">${money(item.quantity * item.price)}</td></tr>`;
            return row;
        };

        // colspan matches AppendTotalRowDesktopOnly — it grows and shrinks with the optional columns.
        const totalColspan = 2 + (!hideQty ? 1 : 0) + (!hidePrice ? 1 : 0);
        const totalRow = (label: string, amount: number) =>
            `<tr style="font-weight:bold;"><td class="invoice-td" colspan="${totalColspan}" style="font-size:12px; padding:8px; text-align:right;">${label}:</td>` +
            `<td class="invoice-td total" style="font-size:12px; padding:8px; text-align:right;">${money(amount)}</td></tr>`;

        let sb = `<style>
.invoice-table { width: 100%; border-collapse: collapse; font-family: Arial, sans-serif; color: #333; }
.invoice-table th.invoice-th, .invoice-table td.invoice-td { vertical-align: top; background: #fff; font-size: 12px; }
.invoice-table th.invoice-th { font-weight: 600; border-bottom: 2px solid #e0e0e0; padding: 8px 2px; white-space: nowrap; }
.sku { width: 15%; min-width: 75px; }
.description { width: 40%; }
.qty { width: 15%; min-width: 75px; }
.price { width: 15%; min-width: 75px; }
.total { width: 15%; min-width: 100px; }
@media only screen and (max-width:600px){
    .invoice-td, .invoice-th { font-size: 10px !important; line-height: 12px !important; padding: 8px 6px; }
}
</style>
<table class="invoice-table" cellpadding="0" cellspacing="0" border="0" width="100%" style="width:100%;"><tbody><thead><tr>`;

        sb += `<th class="invoice-th sku" style="text-align:left;">SKU</th>`;
        sb += `<th class="invoice-th description" style="text-align:left;">Description</th>`;
        if (!hideQty) sb += `<th class="invoice-th qty" style="text-align:right;">Qty</th>`;
        if (!hidePrice) sb += `<th class="invoice-th price" style="text-align:right;">Price</th>`;
        sb += `<th class="invoice-th total" style="text-align:right;">Total</th></tr></thead>`;

        sb += items.map(itemRow).join("");
        sb += totalRow("Item Subtotal", totals.itemSubtotal);
        sb += totalRow("Sales Tax", totals.tax);
        sb += totalRow("Total", totals.total);
        sb += totalRow("Amount Due", totals.total);
        sb += `</tbody></table>`;

        return sb;
    }

    // The look the four accounting templates share, picked on Settings > Email/SMS > Email Options.
    // It is stored on each template row, so the update fans it out to all four server-side.
    async getTemplateTheme(): Promise<ITemplateThemeModel> {
        const result = await this.httpService.get('emailTemplates/getTemplateTheme');
        return {
            themeColor: result?.themeColor || PAYMENT_THEME_DEFAULT_COLOR,
            useLightHeader: !!result?.useLightHeader
        };
    }

    async updateTemplateTheme(model: ITemplateThemeModel): Promise<ITemplateThemeModel> {
        const result = await this.httpService.post('emailTemplates/updateTemplateTheme', model);
        return {
            themeColor: result?.themeColor || PAYMENT_THEME_DEFAULT_COLOR,
            useLightHeader: !!result?.useLightHeader
        };
    }

    async getEstimateTemplateCustomization(): Promise<IEstimateTemplateCustomizationModel> {
        return await this.httpService.get('emailTemplates/getEstimateTemplateCustomization');
    }

    async updateEstimateTemplateCustomization(model: IEstimateTemplateCustomizationModel): Promise<IEstimateTemplateCustomizationModel> {
        return await this.httpService.post('emailTemplates/updateEstimateTemplateCustomization', model);
    }

    // Returns the saved image URL; the caller assigns it to the matching model field and persists via update.
    async updateEstimateTemplateImage(imageKey: string, enc64Image: string): Promise<string> {
        const result = await this.httpService.post('emailTemplates/updateEstimateTemplateImage', { imageKey, image: enc64Image });
        return result?.url;
    }

    // Live-preview render for the estimate editor — mirrors the backend BaseEstimateHTML with sample data.
    getReplacedEstimateEmailHTML(model: IEstimateTemplateCustomizationModel) {
        let html = this.getBaseEstimateEmailRawHTML();

        // Model-driven (real) fields
        html = html.replace("%CUSTOM_HEADER_TEXT%", model?.headerText || "");
        // Escaped to match how InvoiceServices renders the custom notes into the sent email.
        html = html.replace("%CUSTOM_NOTES%", this.escapeHtml(model?.customNotes).replace(/\r\n|\n/g, "<br>"));
        html = html.split("%ESTIMATE_SECTIONS%").join(this.buildEstimateSectionsHtml(model));
        html = html.split("%THEME_COLOR%").join(this.normalizeThemeColor(model?.themeColor));

        const company = GlobalsService.company;
        const user = GlobalsService.userInfo;

        // Mirrors InvoiceServices.PrepareInvoiceEmail: Template Settings → Hide sender info.
        if (company?.hideEstimateSenderInfo)
            html = html.replace(/<!-- Sending user -->[\s\S]*?<!-- \/Sending user -->/, "");

        // Company logo: template override → the real company logo → placeholder
        html = html.split("%COMPANY_LOGO%").join(model?.headerImageUrl || company?.logoUrl || EmailService.LOGO_PLACEHOLDER);
        // Sending user's photo: dropped unless they actually uploaded one.
        html = EmailService.applyProfilePicture(html, "%SENDING_USER_IMAGE%", user?.profilePictureUrl);

        const values: { [key: string]: string } = {
            // Real company info (pulled live from the logged-in company)
            "%COMPANY_NAME%": company?.companyName || "",
            "%COMPANY_ADDRESS1%": company?.address1 || "",
            "%COMPANY_CITY%": company?.city || "",
            "%COMPANY_STATE%": company?.state || "",
            "%COMPANY_POSTAL_CODE%": company?.zipcode || "",
            "%COMPANY_PHONE_NUMBER%": company?.phoneNumber || "",
            "%COMPANY_EMAIL%": company?.email || "",
            // Real sending user (the logged-in user stands in for whoever sends the estimate)
            "%SENDING_USER_NAME%": user?.fullName || "",
            "%SENDING_USER_EMAIL%": user?.emailAddress || "",
            "%SENDING_USER_PHONE%": user?.phoneNumber || "",
            // Customer + quote fields have no real source on the settings page, so these stay as
            // sample placeholders. When the estimate email actually sends, they're filled from the quote/customer.
            "%CUSTOMER_DISPLAY_NAME%": "Sample Customer",
            "%CUSTOMER_ADDRESS1%": "123 Main St",
            "%CUSTOMER_CITY%": "Anytown",
            "%CUSTOMER_STATE%": "ST",
            "%CUSTOMER_ZIP_CODE%": "00000",
            "%CUSTOMER_HOME_PHONE%": "(555) 555-1234",
            "%CUSTOMER_EMAIL%": "customer@example.com",
            // Matches EmailService's %CUSTOMER_CONTACT_LINES%: each contact detail only gets a line
            // when it has a value, so a customer with no phone doesn't leave a blank row behind.
            "%CUSTOMER_CONTACT_LINES%": "<br>(555) 555-1234<br>customer@example.com",
            "%CUSTOMER_FIRST_NAME%": "Sample",
            // Named rather than sampled: the greeting is the one line of the layout the user can't edit,
            // so the preview says which field fills it. (Server-side it resolves first name → last name →
            // company name; first name is what it produces for a normal record.)
            "%CUSTOMER_GREETING_NAME%": "CUSTOMER FIRST NAME",
            "%INVOICE_NUMBER%": "0000",
            "%PO_NUMBER%": "N/A",
            // Count-agnostic lower-case document word, filled from the real quote when the email sends.
            "%DOCUMENT_TYPE%": "estimate(s)",
            "%INVOICE_DATE%": new Date().toLocaleDateString(),
            "%QUOTE_TOTAL%": "$0.00",
            "%VIEW_QUOTE_DETAILS_URL%": "#",
        };
        for (const key of Object.keys(values)) {
            html = html.split(key).join(values[key]);
        }
        return html;
    }

    // Estimate email layout — kept in sync with Services/EmailTemplatesRawHTML.cs BaseEstimateHTML.
    getBaseEstimateEmailRawHTML() {
        return `<!doctype html>
<html lang="und" dir="auto">
<head>
  <meta http-equiv="Content-Type" content="text/html; charset=UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1">
  <title></title>
</head>
<body style="margin:0; padding:0; background:transparent; -webkit-text-size-adjust:100%;">
  <table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" style="background:transparent;">
    <tr>
      <td align="center" style="padding:24px 12px;">
        <table role="presentation" width="600" cellpadding="0" cellspacing="0" border="0" style="width:600px; max-width:600px; background:#ffffff; font-family:Arial, Helvetica, sans-serif; color:#333333;">
          <tr>
            <td style="padding:28px 20px 10px 20px;">
              <table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0">
                <tr>
                  <td valign="top" width="42%" style="font-size:12px; line-height:1.5; color:#333333;">
                    <strong style="font-size:13px;">%CUSTOMER_DISPLAY_NAME%</strong><br>
                    %CUSTOMER_ADDRESS1%<br>
                    %CUSTOMER_CITY%, %CUSTOMER_STATE% %CUSTOMER_ZIP_CODE%%CUSTOMER_CONTACT_LINES%
                  </td>
                  <td valign="top" align="center" width="22%">
                    <img src="%COMPANY_LOGO%" alt="" width="90" style="max-width:90px; height:auto; display:block; margin:0 auto;">
                  </td>
                  <td valign="top" align="right" width="36%" style="font-size:12px; line-height:1.7; color:#333333;">
                    <strong>Estimate #:</strong> %INVOICE_NUMBER%<br>
                    <strong>PO #:</strong> %PO_NUMBER%<br>
                    <strong>Estimate Date:</strong> %INVOICE_DATE%<br>
                    <strong>Quote Total:</strong> %QUOTE_TOTAL%
                  </td>
                </tr>
              </table>
            </td>
          </tr>
          <tr>
            <td style="padding:10px 20px 18px 20px; font-size:13px; line-height:1.6; color:#333333;">
              <p style="margin:0 0 14px 0;"><strong>Hi %CUSTOMER_GREETING_NAME%,</strong></p>
              <!-- Same ~5in / 480px ceiling as the Final Service Template's blue banner, so a long
                   header can't push the rest of the estimate off the first screen. -->
              <div style="max-height:400px; overflow:hidden;">%CUSTOM_HEADER_TEXT%</div>
              <div>%CUSTOM_NOTES%</div>
            </td>
          </tr>
          <tr>
            <td style="padding:6px 20px 26px 20px;">
              <table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0">
                <tr>
                  <!-- Sending user -->
                  <!-- The two comments either side of this cell bracket what InvoiceServices.PrepareInvoiceEmail
                       cuts out for a company with Hide sender info on (Template Settings). The company block
                       then has the row to itself and stays on the right. -->
                  <td valign="middle" width="50%">
                    <table role="presentation" cellpadding="0" cellspacing="0" border="0">
                      <tr>
                        <td valign="middle" style="padding-right:12px;">
                          <img src="%SENDING_USER_IMAGE%" alt="" width="56" height="56" style="width:56px; height:56px; border-radius:50%; display:block;">
                        </td>
                        <td valign="middle" style="font-size:12px; line-height:1.5; color:#333333;">
                          <strong>%SENDING_USER_NAME%</strong><br>
                          %SENDING_USER_EMAIL%<br>
                          %SENDING_USER_PHONE%
                        </td>
                      </tr>
                    </table>
                  </td>
                  <!-- /Sending user -->
                  <td valign="middle" align="right" width="50%">
                    <table role="presentation" cellpadding="0" cellspacing="0" border="0" align="right">
                      <tr>
                        <td valign="middle" style="padding-right:12px;">
                          <img src="%COMPANY_LOGO%" alt="" width="54" style="max-width:54px; height:auto; display:block;">
                        </td>
                        <td valign="middle" style="font-size:12px; line-height:1.5; color:#333333;">
                          <strong>%COMPANY_NAME%</strong><br>
                          %COMPANY_ADDRESS1%<br>
                          %COMPANY_CITY%, %COMPANY_STATE% %COMPANY_POSTAL_CODE%<br>
                          %COMPANY_PHONE_NUMBER%<br>
                          %COMPANY_EMAIL%
                        </td>
                      </tr>
                    </table>
                  </td>
                </tr>
              </table>
            </td>
          </tr>
          <!-- The outer td carries the 20px gutter that puts the bar on the same 560px column as the
               tiles row and the promo banners below it. -->
          <tr>
            <td style="padding:0 20px;">
              <table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" style="background:#%THEME_COLOR%;">
                <tr>
                  <td valign="middle" style="color:#ffffff;">
                    <a href="%VIEW_QUOTE_DETAILS_URL%" target="_blank" rel="noopener noreferrer" style="display:block; padding:22px 12px 22px 24px; color:#ffffff; text-decoration:none;">
                      <div style="font-size:22px; font-weight:bold; line-height:1.2; color:#ffffff;">Estimate #: %INVOICE_NUMBER%</div>
                      <div style="font-size:12px; margin-top:8px; color:#ffffff;"><strong>PO #:</strong> %PO_NUMBER% &nbsp;&nbsp;&nbsp; <strong>Estimate Date:</strong> %INVOICE_DATE%</div>
                    </a>
                  </td>
                  <td valign="middle" align="right" style="color:#ffffff;">
                    <a href="%VIEW_QUOTE_DETAILS_URL%" target="_blank" rel="noopener noreferrer" style="display:block; padding:22px 24px 22px 12px; color:#ffffff; text-decoration:none; text-align:right;">
                      <div style="font-size:22px; font-weight:bold; line-height:1.2; color:#ffffff;">%QUOTE_TOTAL%</div>
                      <div style="font-size:12px; margin-top:8px; color:#ffffff; text-decoration:underline;">VIEW ESTIMATE DETAILS</div>
                    </a>
                  </td>
                </tr>
              </table>
            </td>
          </tr>
          <tr><td>%ESTIMATE_SECTIONS%</td></tr>
        </table>
      </td>
    </tr>
  </table>
</body>
</html>`;
    }

    // Payment receipt layout — kept in sync with Services/EmailTemplatesRawHTML.cs BasePaymentReceiptHTML.
    getBasePaymentEmailRawHTML() {
        return `<!doctype html>
<html lang="und" dir="auto">
<head>
  <meta http-equiv="Content-Type" content="text/html; charset=UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1">
  <title></title>
</head>
<body style="margin:0; padding:0; background:#f4f4f6; -webkit-text-size-adjust:100%;">
  <table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" style="background:#f4f4f6;">
    <tr>
      <td align="center" style="padding:24px 12px;">
        <table role="presentation" width="600" cellpadding="0" cellspacing="0" border="0" style="width:600px; max-width:600px; background:#ffffff; font-family:Arial, Helvetica, sans-serif; color:#333333;">

          <!-- Header band: logo | title + subtitle -->
          <tr>
            <td style="background:#%HEADER_BG%; padding:26px 24px;">
              <table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0">
                <tr>
                  <td valign="middle" width="40%" style="padding-right:16px;">
                    <img src="%COMPANY_LOGO%" alt="" width="150" style="max-width:150px; height:auto; display:block;">
                  </td>
                  <td valign="middle" style="padding-left:24px; color:#%HEADER_TEXT%;">
                    <div style="font-size:26px; font-weight:bold; line-height:1.2; color:#%HEADER_TEXT%;">%PAYMENT_HEADER_TITLE%</div>
                    <div style="font-size:13px; margin-top:8px; color:#%HEADER_TEXT%;">%PAYMENT_HEADER_SUBTITLE%</div>
                  </td>
                </tr>
              </table>
            </td>
          </tr>

          <!-- Meta row: payment date | payment method | receipt number -->
          <tr>
            <td style="padding:20px 24px 0 24px;">
              <table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0">
                <tr>
                  <td valign="top" width="34%" style="padding-right:10px;">
                    <table role="presentation" cellpadding="0" cellspacing="0" border="0">
                      <tr>
                        <td valign="middle" width="42" style="width:42px;">
                          <div style="width:42px; height:42px; border-radius:21px; background:#%THEME_TINT%; text-align:center; font-size:0; line-height:0;">
                            <img src="data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='22' height='22' viewBox='0 0 24 24' fill='none' stroke='%23%THEME_COLOR%' stroke-width='1.7' stroke-linecap='round' stroke-linejoin='round'><rect x='3' y='5' width='18' height='16' rx='2'/><path d='M3 10h18'/><path d='M8 3v4'/><path d='M16 3v4'/><path d='M12 13.2v4.6'/><path d='M13.5 14.3c-.6-.7-3-.8-3 .5 0 1.4 3 .6 3 2 0 1.3-2.4 1.2-3 .5'/></svg>" width="22" height="22" alt="" style="width:22px; height:22px; display:inline-block; margin-top:10px;">
                          </div>
                        </td>
                        <td valign="middle" style="padding-left:10px;">
                          <div style="font-size:12px; font-weight:bold; color:#%THEME_COLOR%;">Payment Date</div>
                          <div style="font-size:13px; font-weight:bold; color:#333333; margin-top:3px;">%PAYMENT_DATE%</div>
                        </td>
                      </tr>
                    </table>
                  </td>
                  <td valign="top" width="33%" style="padding-right:10px; border-left:1px solid #e6e6ea; padding-left:14px;">
                    <table role="presentation" cellpadding="0" cellspacing="0" border="0">
                      <tr>
                        <td valign="middle" width="42" style="width:42px;">
                          <div style="width:42px; height:42px; border-radius:21px; background:#%THEME_TINT%; text-align:center; font-size:0; line-height:0;">
                            <img src="data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='22' height='22' viewBox='0 0 24 24' fill='none' stroke='%23%THEME_COLOR%' stroke-width='1.7' stroke-linecap='round' stroke-linejoin='round'><rect x='2' y='5' width='20' height='14' rx='2.5'/><path d='M2 10h20'/><path d='M6 15h3.5'/></svg>" width="22" height="22" alt="" style="width:22px; height:22px; display:inline-block; margin-top:10px;">
                          </div>
                        </td>
                        <td valign="middle" style="padding-left:10px;">
                          <div style="font-size:12px; font-weight:bold; color:#%THEME_COLOR%;">Payment Method</div>
                          <div style="font-size:13px; font-weight:bold; color:#333333; margin-top:3px;">%PAYMENT_METHOD%</div>
                          %PAYMENT_AUTH_LINE%
                        </td>
                      </tr>
                    </table>
                  </td>
                  <td valign="top" width="33%" style="border-left:1px solid #e6e6ea; padding-left:14px;">
                    <table role="presentation" cellpadding="0" cellspacing="0" border="0">
                      <tr>
                        <td valign="middle" width="42" style="width:42px;">
                          <div style="width:42px; height:42px; border-radius:21px; background:#%THEME_TINT%; text-align:center; font-size:0; line-height:0;">
                            <img src="data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='22' height='22' viewBox='0 0 24 24' fill='none' stroke='%23%THEME_COLOR%' stroke-width='1.7' stroke-linecap='round' stroke-linejoin='round'><path d='M6 3h12v18l-2-1.5-2 1.5-2-1.5-2 1.5-2-1.5-2 1.5z'/><path d='M9.5 8h5'/><path d='M9.5 12h5'/><path d='M9.5 16h2.5'/></svg>" width="22" height="22" alt="" style="width:22px; height:22px; display:inline-block; margin-top:10px;">
                          </div>
                        </td>
                        <td valign="middle" style="padding-left:10px;">
                          <div style="font-size:12px; font-weight:bold; color:#%THEME_COLOR%;">Receipt Number</div>
                          <div style="font-size:13px; font-weight:bold; color:#333333; margin-top:3px;">%PAYMENT_REFERENCE_NUMBER%</div>
                          <div style="font-size:11px; color:#%THEME_COLOR%; margin-top:3px;">Thank you for your business!</div>
                        </td>
                      </tr>
                    </table>
                  </td>
                </tr>
              </table>
            </td>
          </tr>

          <!-- Divider under the meta row -->
          <tr><td style="padding:16px 24px 0 24px;"><div style="border-top:1px solid #e6e6ea; font-size:0; line-height:0;">&nbsp;</div></td></tr>

          <!-- Paid by card | Total paid card -->
          <tr>
            <td style="padding:18px 24px 0 24px;">
              <table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0">
                <tr>
                  <td valign="middle" width="52%" style="background:#%THEME_TINT%; padding:18px 20px; border-radius:10px 0 0 10px;">
                    <table role="presentation" cellpadding="0" cellspacing="0" border="0">
                      <tr>
                        <td valign="middle" width="56" style="width:56px;">
                          <div style="width:56px; height:56px; border-radius:28px; background:#%THEME_COLOR%; text-align:center; font-size:0; line-height:0;">
                            <img src="data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='34' height='34' viewBox='0 0 64 64'><circle cx='32' cy='23' r='12' fill='%23ffffff'/><path d='M9 57c0-12.7 10.3-19 23-19s23 6.3 23 19z' fill='%23ffffff'/></svg>" width="34" height="34" alt="" style="width:34px; height:34px; display:inline-block; margin-top:11px;">
                          </div>
                        </td>
                        <td valign="middle" style="padding-left:14px; font-size:12px; line-height:1.5; color:#333333;">
                          <div style="color:#%THEME_COLOR%; margin-bottom:6px;">Paid by</div>
                          <strong style="font-size:13px;">%CUSTOMER_DISPLAY_NAME%</strong><br>
                          %CUSTOMER_ADDRESS1%<br>
                          %CUSTOMER_CITY%, %CUSTOMER_STATE% %CUSTOMER_ZIP_CODE%
                        </td>
                      </tr>
                    </table>
                  </td>
                  <td valign="middle" align="center" width="48%" style="background:#%THEME_COLOR%; padding:18px 20px; color:#ffffff; text-align:center; border-radius:0 10px 10px 0;">
                    <div style="font-size:13px; font-weight:bold; color:#ffffff;">Total Paid</div>
                    <div style="font-size:30px; font-weight:bold; color:#ffffff; padding:6px 0;">%PAYMENT_AMOUNT%</div>
                    <div style="border-top:1px solid #ffffff; font-size:0; line-height:0;">&nbsp;</div>
                    <div style="font-size:13px; font-weight:bold; color:#ffffff; padding-top:8px;">Payment Successful</div>
                    <div style="font-size:12px; color:#ffffff; padding-top:3px;">Your payment has been processed</div>
                  </td>
                </tr>
              </table>
            </td>
          </tr>

          <!-- Payment summary -->
          <tr>
            <td style="padding:24px 24px 0 24px;">
              <table role="presentation" cellpadding="0" cellspacing="0" border="0">
                <tr>
                  <td valign="middle" width="20" style="width:20px; font-size:0; line-height:0;">
                    <img src="data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='20' height='20' viewBox='0 0 24 24' fill='none' stroke='%23%THEME_COLOR%' stroke-width='1.7' stroke-linecap='round' stroke-linejoin='round'><path d='M6 3h12v18l-2-1.5-2 1.5-2-1.5-2 1.5-2-1.5-2 1.5z'/><path d='M9.5 8h5'/><path d='M9.5 12h5'/><path d='M9.5 16h2.5'/></svg>" width="20" height="20" alt="" style="width:20px; height:20px; display:block;">
                  </td>
                  <td valign="middle" style="padding-left:8px; font-size:14px; font-weight:bold; color:#%THEME_COLOR%;">Payment Summary</td>
                </tr>
              </table>
              <div style="border-top:1px solid #e6e6ea; margin-top:12px; font-size:0; line-height:0;">&nbsp;</div>
            </td>
          </tr>
          <tr>
            <td style="padding:0 24px;">
              %PAYMENT_SUMMARY_ROWS%
            </td>
          </tr>

          <!-- Custom notes -->
          %PAYMENT_CUSTOM_NOTES%

          <!-- 'Protect your investment' call to action -->
          %PAYMENT_CTA%

          <!-- Footer link row -->
          %PAYMENT_FOOTER_LINKS%

          <!-- Footer: logo, company details, closing line -->
          <tr>
            <td style="padding:0 24px;"><div style="border-top:3px solid #%THEME_COLOR%; font-size:0; line-height:0;">&nbsp;</div></td>
          </tr>
          <tr>
            <td style="padding:18px 24px 0 24px;">
              <table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0">
                <tr>
                  <td valign="middle" width="30%">
                    <img src="%COMPANY_LOGO%" alt="" width="110" style="max-width:110px; height:auto; display:block;">
                  </td>
                  <td valign="middle" align="center" style="font-size:12px; line-height:1.6; color:#333333;">
                    <strong>%COMPANY_NAME%</strong><br>
                    %COMPANY_ADDRESS1%<br>
                    %COMPANY_CITY%, %COMPANY_STATE% %COMPANY_POSTAL_CODE%<br>
                    %COMPANY_PHONE_NUMBER%
                  </td>
                </tr>
              </table>
            </td>
          </tr>
          <tr>
            <td style="padding:16px 24px 6px 24px;">
              <table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0">
                <tr>
                  <td valign="middle" align="center" style="font-size:13px; font-weight:bold; color:#%THEME_COLOR%;">%PAYMENT_FOOTER_THANK_YOU%</td>
                </tr>
              </table>
            </td>
          </tr>
          <tr>
            <td align="right" style="padding:0 24px 20px 24px;">
              <a href="https://www.skedit.com" target="_blank" rel="noopener noreferrer">
                <img src="https://docs.skedit.com/powered-by-skedit.png" width="90" alt="Powered by Skedit" style="display:inline-block; border:0;">
              </a>
            </td>
          </tr>

        </table>
      </td>
    </tr>
  </table>
</body>
</html>`;
    }

    async getBatchTemplateCustomization(): Promise<IBatchTemplateCustomizationModel> {
        return await this.httpService.get('emailTemplates/getBatchTemplateCustomization');
    }

    async updateBatchTemplateCustomization(model: IBatchTemplateCustomizationModel): Promise<IBatchTemplateCustomizationModel> {
        return await this.httpService.post('emailTemplates/updateBatchTemplateCustomization', model);
    }

    // Live-preview render for the batch editor. Mirrors BaseBatchHTML plus InvoiceServices'
    // BuildBatchRowsHtml, with a sample batch standing in for the real invoices.
    getReplacedBatchEmailHTML(model: IBatchTemplateCustomizationModel) {
        let html = this.getBaseBatchEmailRawHTML();

        const themeColor = this.normalizeThemeColor(model?.themeColor);
        html = html.split("%THEME_COLOR%").join(themeColor);
        html = html.split("%CUSTOM_HEADER_TEXT%").join(model?.headerText || "");
        html = html.split("%BATCH_BAR_TITLE%").join(this.escapeHtml(model?.barTitle));
        html = html.split("%BATCH_ROWS%").join(this.buildBatchRowsHtml(themeColor));

        // The real button only renders for an invoice batch with a balance when online payments are on;
        // the preview always shows it, since that's the case worth designing against.
        const payLabel = this.escapeHtml(model?.payButtonText || "MAKE PAYMENT");
        html = html.split("%BATCH_PAY_BUTTON%").join(
            `<tr><td align="right" style="padding:24px 20px 0 20px;">` +
            `<a href="#" style="display:inline-block; background:#00ff00; color:#000000; font-family:Arial,sans-serif; font-size:13px; ` +
            `font-weight:bold; line-height:120%; text-decoration:none; padding:14px 26px; border-radius:4px;">${payLabel}</a>` +
            `</td></tr>`);

        const company = GlobalsService.company;
        const user = GlobalsService.userInfo;

        // Mirrors InvoiceServices.PrepareBatchInvoiceEmail: Template Settings > Hide the sender's details.
        // Every marked span goes (photo, name, email), hence the global flag.
        if (company?.hideEstimateSenderInfo)
            html = html.replace(/<!-- Sending user -->[\s\S]*?<!-- \/Sending user -->/g, "");

        html = html.split("%COMPANY_LOGO%").join(model?.headerImageUrl || company?.logoUrl || EmailService.LOGO_PLACEHOLDER);
        // Sending user's photo: dropped unless they actually uploaded one.
        html = EmailService.applyProfilePicture(html, "%SENDING_USER_IMAGE%", user?.profilePictureUrl);

        const values: { [key: string]: string } = {
            "%COMPANY_NAME%": company?.companyName || "",
            "%COMPANY_ADDRESS1%": company?.address1 || "",
            "%COMPANY_CITY%": company?.city || "",
            "%COMPANY_STATE%": company?.state || "",
            "%COMPANY_POSTAL_CODE%": company?.zipcode || "",
            "%COMPANY_PHONE_NUMBER%": company?.phoneNumber || "",
            "%COMPANY_EMAIL%": company?.email || "",
            "%SENDING_USER_NAME%": user?.fullName || "",
            "%SENDING_USER_EMAIL%": user?.emailAddress || "",
            "%CUSTOMER_DISPLAY_NAME%": "Sample Customer",
            "%CUSTOMER_ADDRESS1%": "123 Main St",
            "%CUSTOMER_CITY%": "Anytown",
            "%CUSTOMER_STATE%": "ST",
            "%CUSTOMER_ZIP_CODE%": "00000",
            "%CUSTOMER_CONTACT_LINES%": "<br>(555) 555-1234<br>customer@example.com",
            "%CUSTOMER_GREETING_NAME%": "CUSTOMER FIRST NAME",
            "%BATCH_DATE_RANGE%": "7/29/26 - 8/3/2026",
            // The batch template is shared by the estimate / sales order / invoice batches; the real
            // send fills this from the documents in the batch. The preview stands in with invoices.
            "%DOCUMENT_TYPE%": "invoice(s)",
        };
        for (const key of Object.keys(values))
            html = html.split(key).join(values[key]);

        return html;
    }

    // Mirrors InvoiceServices.BuildBatchRowsHtml with a sample batch.
    private buildBatchRowsHtml(themeColor: string): string {
        const tint = this.tintThemeColor(themeColor, .12);
        const rows = [
            { invoice: "123456", po: "Johnson", amount: 5456.22, paid: 0 },
            { invoice: "198655", po: "Johnson Track", amount: 8599.15, paid: 0 },
            { invoice: "324556", po: "Sullivan Eclipse", amount: 4765.44, paid: 4765.44 },
            { invoice: "465778", po: "John Malone", amount: 6924.63, paid: 6924.63 },
            { invoice: "123445", po: "Mccormick", amount: 7218.89, paid: 0 },
        ];
        const money = (n: number) => n.toLocaleString(undefined, { style: "currency", currency: "USD" });

        let body = "";
        let total = 0;
        for (const r of rows) {
            total += r.amount;
            const paidCell = r.paid > 0
                ? `<td align="right" valign="top" style="padding:12px 6px; font-size:12px; font-weight:bold; color:#1B8A3A;">${money(r.paid)}</td>`
                : `<td align="right" valign="top" style="padding:12px 6px;">&nbsp;</td>`;
            body += `<tr>` +
                `<td align="left" valign="top" style="padding:12px 6px; font-size:12px; color:#333333;">${r.invoice}</td>` +
                `<td align="left" valign="top" style="padding:12px 6px; font-size:12px; color:#333333;">${r.po}</td>` +
                `<td align="right" valign="top" style="padding:12px 6px; font-size:12px; color:#333333;">${money(r.amount)}</td>` +
                paidCell + `</tr>` +
                `<tr><td colspan="4" style="border-top:1px solid #e6e6ea; font-size:0; line-height:0;">&nbsp;</td></tr>`;
        }

        const header = `<tr>` +
            `<td align="left" style="padding:12px 6px; font-size:12px; font-weight:bold; color:#333333;">Invoice</td>` +
            `<td align="left" style="padding:12px 6px; font-size:12px; font-weight:bold; color:#333333;">P.O. #</td>` +
            `<td align="right" style="padding:12px 6px; font-size:12px; font-weight:bold; color:#333333;">Amount</td>` +
            `<td align="right" style="padding:12px 6px; font-size:12px; font-weight:bold; color:#333333;">Payment</td>` +
            `</tr>` +
            `<tr><td colspan="4" style="border-top:2px solid #${tint}; font-size:0; line-height:0;">&nbsp;</td></tr>`;

        return `<table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" style="width:100%; font-family:Arial, Helvetica, sans-serif;">${header}${body}</table>` +
            `<table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" style="width:100%; background:#${tint}; margin-top:12px;"><tr>` +
            `<td align="right" style="padding:18px 12px; font-size:16px; font-weight:bold; color:#${themeColor};">Batch Total</td>` +
            `<td align="right" width="150" style="width:150px; padding:18px 20px 18px 12px; font-size:18px; font-weight:bold; color:#333333;">${money(total)}</td>` +
            `</tr></table>`;
    }

    // Batch email layout — kept in sync with Services/EmailTemplatesRawHTML.cs BaseBatchHTML.
    getBaseBatchEmailRawHTML() {
        return `<!doctype html>
<html lang="und" dir="auto">
<head>
  <meta http-equiv="Content-Type" content="text/html; charset=UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1">
  <title></title>
</head>
<body style="margin:0; padding:0; background:transparent; -webkit-text-size-adjust:100%;">
  <table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" style="background:transparent;">
    <tr>
      <td align="center" style="padding:24px 12px;">
        <table role="presentation" width="600" cellpadding="0" cellspacing="0" border="0" style="width:600px; max-width:600px; background:#ffffff; font-family:Arial, Helvetica, sans-serif; color:#333333;">

          <!-- Header: customer / logo / company -->
          <tr>
            <td style="padding:28px 20px 10px 20px;">
              <table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0">
                <tr>
                  <td valign="top" width="38%" style="font-size:12px; line-height:1.5; color:#%THEME_COLOR%;">
                    <strong style="font-size:13px;">%CUSTOMER_DISPLAY_NAME%</strong><br>
                    %CUSTOMER_ADDRESS1%<br>
                    %CUSTOMER_CITY%, %CUSTOMER_STATE% %CUSTOMER_ZIP_CODE%%CUSTOMER_CONTACT_LINES%
                  </td>
                  <td valign="top" align="center" width="24%">
                    <img src="%COMPANY_LOGO%" alt="" width="90" style="max-width:90px; height:auto; display:block; margin:0 auto;">
                  </td>
                  <td valign="top" align="right" width="38%" style="font-size:12px; line-height:1.5; color:#%THEME_COLOR%;">
                    <strong style="font-size:13px;">%COMPANY_NAME%</strong><br>
                    %COMPANY_ADDRESS1%<br>
                    %COMPANY_CITY%, %COMPANY_STATE% %COMPANY_POSTAL_CODE%<br>
                    %COMPANY_PHONE_NUMBER%<br>
                    %COMPANY_EMAIL%
                  </td>
                </tr>
              </table>
            </td>
          </tr>

          <!-- Greeting + body copy. Near-black rather than the theme colour: this is the block the
               company writes its own message into, so it reads as plain body text. -->
          <tr>
            <td style="padding:14px 20px 18px 20px; font-size:13px; line-height:1.6; color:#333333;">
              <p style="margin:0 0 14px 0;">Hello %CUSTOMER_GREETING_NAME%,</p>
              <!-- Same ~5in / 480px ceiling as the other templates, so a long body can't push the
                   batch table off the first screen. -->
              <div style="max-height:400px; overflow:hidden;">%CUSTOM_HEADER_TEXT%</div>
            </td>
          </tr>

          <!-- Signature: sending user / company. The Sending user markers wrap what Template Settings > Hide
               the sender's details takes out (photo, name, email); the company lines always stay. -->
          <tr>
            <td style="padding:6px 20px 26px 20px;">
              <table role="presentation" cellpadding="0" cellspacing="0" border="0">
                <tr>
                  <!-- Sending user -->
                  <td valign="middle" style="padding-right:16px;">
                    <img src="%SENDING_USER_IMAGE%" alt="" width="96" height="96" style="width:96px; height:96px; border-radius:50%; display:block;">
                  </td>
                  <!-- /Sending user -->
                  <td valign="middle" style="font-size:13px; line-height:1.6; color:#%THEME_COLOR%;">
                    <!-- Sending user -->%SENDING_USER_NAME%<br><!-- /Sending user -->
                    <strong>%COMPANY_NAME%</strong><br>
                    %COMPANY_ADDRESS1% %COMPANY_CITY%, %COMPANY_STATE% %COMPANY_POSTAL_CODE%<br>
                    %COMPANY_PHONE_NUMBER%<!-- Sending user --><br>
                    %SENDING_USER_EMAIL%<!-- /Sending user -->
                  </td>
                </tr>
              </table>
            </td>
          </tr>

          <!-- Batch bar: title + the date range the batch covers -->
          <tr>
            <td style="padding:0 20px;">
              <table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" style="background:#%THEME_COLOR%;">
                <tr>
                  <td valign="middle" style="padding:22px 12px 22px 24px; font-size:20px; font-weight:bold; color:#ffffff;">%BATCH_BAR_TITLE%</td>
                  <td valign="middle" align="right" style="padding:22px 24px 22px 12px; font-size:16px; color:#ffffff;">%BATCH_DATE_RANGE%</td>
                </tr>
              </table>
            </td>
          </tr>

          <!-- Batch table + total -->
          <tr>
            <td style="padding:6px 20px 0 20px;">%BATCH_ROWS%</td>
          </tr>

          <!-- Payment button -->
          %BATCH_PAY_BUTTON%

          <tr>
            <td align="right" style="padding:18px 20px 24px 20px;">
              <a href="https://www.skedit.com" target="_blank" rel="noopener noreferrer">
                <img src="https://docs.skedit.com/powered-by-skedit.png" width="90" alt="Powered by Skedit" style="display:inline-block; border:0;">
              </a>
            </td>
          </tr>

        </table>
      </td>
    </tr>
  </table>
</body>
</html>`;
    }

    private buildEstimateTilesHtml(model: IEstimateTemplateCustomizationModel): string {
        if (!model) return "";
        const tiles = [
            { image: model.tile1ImageUrl, text: model.tile1Text, url: model.tile1Url },
            { image: model.tile2ImageUrl, text: model.tile2Text, url: model.tile2Url },
            { image: model.tile3ImageUrl, text: model.tile3Text, url: model.tile3Url },
            { image: model.tile4ImageUrl, text: model.tile4Text, url: model.tile4Url },
        ].filter(x => (x.image && x.image.trim()) || (x.text && x.text.trim()));

        if (tiles.length === 0) return "";

        // Company-chosen icon size (px); 0/unset falls back to a balanced default.
        const iconSize = model.tileImageSize && model.tileImageSize > 0 ? model.tileImageSize : 64;

        // Equal-width columns across the full 560px band rather than a shrink-to-fit centered cluster: each
        // icon is centred in its own column, so the gaps between tiles stay identical whether the labels are
        // long, short, or absent entirely. A cluster would only be as wide as its contents, which is what
        // left the label-less rows bunched in the middle with dead space either side.
        const cellWidth = Number((100 / tiles.length).toFixed(2));

        let cells = "";
        for (const tile of tiles) {
            // Icon on top, label centered beneath it. Keeping the text under the icon (instead of beside it)
            // means a long label can't shove the icon off to one side, so every tile stays symmetric and the
            // row reads as evenly spaced even when labels wrap to two lines.
            let inner = "";
            if (tile.image && tile.image.trim())
                inner += `<img src="${tile.image}" width="${iconSize}" height="${iconSize}" style="display:block; margin:0 auto; border:0;" alt="" />`;
            if (tile.text && tile.text.trim())
                inner += `<div style="margin:6px auto 0 auto; max-width:100px; font-family:Arial,sans-serif; font-size:12px; font-weight:bold; color:#333333; line-height:1.3;">${this.escapeHtml(tile.text)}</div>`;

            const content = (tile.url && tile.url.trim())
                ? `<a href="${this.escapeHtml(tile.url)}" target="_blank" rel="noopener noreferrer" style="display:block; text-align:center; text-decoration:none; color:#333333;">${inner}</a>`
                : inner;
            cells += `<td align="center" valign="top" width="${cellWidth}%" style="width:${cellWidth}%; padding:8px 10px;">${content}</td>`;
        }
        // Outer band keeps the white background; the 20px gutter on the inner td puts the tiles on the same
        // 560px column as the estimate bar above and the promo banners below.
        return `<table role="presentation" width="100%" cellspacing="0" cellpadding="0" border="0" style="max-width:600px; margin:0 auto; background:#ffffff;"><tr><td style="padding:18px 20px;"><table role="presentation" width="100%" cellspacing="0" cellpadding="0" border="0" style="width:100%;"><tr>${cells}</tr></table></td></tr></table>`;
    }

    private buildEstimateBannerHtml(image: string, url: string): string {
        if (!image || !image.trim()) return "";

        let img = `<img src="${image}" width="560" style="width:100%; max-width:560px; height:auto; display:block; margin:0 auto; border:0;" alt="" />`;
        if (url && url.trim())
            img = `<a href="${this.escapeHtml(url)}" target="_blank" rel="noopener noreferrer">${img}</a>`;

        // The 20px gutter keeps the banner on the same 560px column as the estimate bar and the tiles row
        // even when the 600px shell shrinks on a phone (where width:100% would otherwise go edge to edge).
        return `<table role="presentation" width="100%" cellspacing="0" cellpadding="0" border="0" style="max-width:600px; margin:0 auto; background:#ffffff;"><tr><td align="center" style="padding:10px 20px;">${img}</td></tr></table>`;
    }

    // Mirrors InvoiceServices.BuildEstimateSectionsHtml — tiles + banners in the configured order.
    // Array.sort is stable, so equal/unset sort orders keep the canonical tiles → banner 1 → banner 2 layout.
    private buildEstimateSectionsHtml(model: IEstimateTemplateCustomizationModel): string {
        if (!model) return "";

        return [
            { sortOrder: model.tilesSortOrder || 0, html: this.buildEstimateTilesHtml(model) },
            { sortOrder: model.banner1SortOrder || 0, html: this.buildEstimateBannerHtml(model.banner1ImageUrl, model.banner1Url) },
            { sortOrder: model.banner2SortOrder || 0, html: this.buildEstimateBannerHtml(model.banner2ImageUrl, model.banner2Url) },
        ].sort((a, b) => a.sortOrder - b.sortOrder)
            .map(x => x.html)
            .join("");
    }

    async getPaymentTemplateCustomization(): Promise<IPaymentTemplateCustomizationModel> {
        return await this.httpService.get('emailTemplates/getPaymentTemplateCustomization');
    }

    async updatePaymentTemplateCustomization(model: IPaymentTemplateCustomizationModel): Promise<IPaymentTemplateCustomizationModel> {
        return await this.httpService.post('emailTemplates/updatePaymentTemplateCustomization', model);
    }

    // Returns the saved image URL; the caller assigns it to the model and persists via update.
    // Returns the saved image URL; the caller assigns it to the model and persists via update.
    async updateBatchTemplateImage(imageKey: string, enc64Image: string): Promise<string> {
        const result = await this.httpService.post('emailTemplates/updateBatchTemplateImage', { imageKey, image: enc64Image });
        return result?.url;
    }

    async updatePaymentTemplateImage(imageKey: string, enc64Image: string): Promise<string> {
        const result = await this.httpService.post('emailTemplates/updatePaymentTemplateImage', { imageKey, image: enc64Image });
        return result?.url;
    }

    // Mirrors Models/PaymentTemplateTheme.cs Normalize — anything unparseable falls back to the default so
    // the preview can't paint an invalid CSS color.
    private normalizeThemeColor(hex: string): string {
        let value = (hex || "").trim().replace(/^#/, "");

        if (value.length === 3)
            value = value[0] + value[0] + value[1] + value[1] + value[2] + value[2];

        if (!/^[0-9a-fA-F]{6}$/.test(value))
            return PAYMENT_THEME_DEFAULT_COLOR;

        return value.toUpperCase();
    }

    // Mirrors Models/PaymentTemplateTheme.cs Tint — mixes toward white, `amount` is how much of the
    // original color survives.
    private tintThemeColor(hex: string, amount: number): string {
        const value = this.normalizeThemeColor(hex);
        const mix = (channel: number) => Math.min(255, Math.max(0, Math.round(255 - ((255 - channel) * amount))));

        const r = mix(parseInt(value.substring(0, 2), 16));
        const g = mix(parseInt(value.substring(2, 4), 16));
        const b = mix(parseInt(value.substring(4, 6), 16));

        const hexPart = (n: number) => n.toString(16).padStart(2, "0").toUpperCase();
        return `${hexPart(r)}${hexPart(g)}${hexPart(b)}`;
    }

    // Live-preview render for the payment-receipt editor. Mirrors the backend BasePaymentReceiptHTML plus the
    // PaymentServices builders, with a sample payment standing in for the real one.
    getReplacedPaymentEmailHTML(model: IPaymentTemplateCustomizationModel) {
        let html = this.getBasePaymentEmailRawHTML();

        const themeColor = this.normalizeThemeColor(model?.themeColor);
        const tint = this.tintThemeColor(themeColor, .12);

        html = html.split("%THEME_COLOR%").join(themeColor);
        html = html.split("%THEME_TINT%").join(tint);

        // Header band only. Mirrors PaymentServices.BuildPaymentReceiptHtml.
        html = html.split("%HEADER_BG%").join(model?.useLightHeader ? "FFFFFF" : themeColor);
        html = html.split("%HEADER_TEXT%").join(model?.useLightHeader ? themeColor : "FFFFFF");

        html = html.split("%PAYMENT_HEADER_TITLE%").join(this.escapeHtml(model?.headerTitle || "Payment Receipt"));
        html = html.split("%PAYMENT_HEADER_SUBTITLE%").join(this.escapeHtml(model?.headerSubtitle));
        html = html.split("%PAYMENT_FOOTER_THANK_YOU%").join(this.escapeHtml(model?.footerThankYouText));

        html = html.split("%PAYMENT_SUMMARY_ROWS%").join(this.buildPaymentSummaryRowsHtml(themeColor));
        html = html.split("%PAYMENT_CUSTOM_NOTES%").join(this.buildPaymentCustomNotesHtml(model));
        html = html.split("%PAYMENT_CTA%").join(this.buildPaymentCtaHtml(model, themeColor));
        html = html.split("%PAYMENT_FOOTER_LINKS%").join(this.buildPaymentFooterLinksHtml(model, themeColor));

        const company = GlobalsService.company;

        // Template logo → the real company logo → placeholder
        html = html.split("%COMPANY_LOGO%").join(model?.headerImageUrl || company?.logoUrl || EmailService.LOGO_PLACEHOLDER);

        const values: { [key: string]: string } = {
            // Real company info (pulled live from the logged-in company)
            "%COMPANY_NAME%": company?.companyName || "",
            "%COMPANY_ADDRESS1%": company?.address1 || "",
            "%COMPANY_CITY%": company?.city || "",
            "%COMPANY_STATE%": company?.state || "",
            "%COMPANY_POSTAL_CODE%": company?.zipcode || "",
            "%COMPANY_ADDRESS2%": company?.address2 || "",
            "%COMPANY_PHONE_NUMBER%": company?.phoneNumber || "",
            "%COMPANY_EMAIL%": company?.email || "",
            "%COMPANY_FULL_ADDRESS%": [company?.address1, company?.city, company?.state, company?.zipcode]
                .filter(x => x && x.trim()).join(", "),
            // The Angular company model doesn't carry the website, so this one is a sample even though
            // the sent email fills it for real.
            "%COMPANY_WEBSITE%": "www.example.com",
            // Customer + payment fields have no real source on a settings page, so these stay samples.
            // When the receipt actually sends they're filled from the payment and its customer.
            //
            // Every token the merge-field picker offers for this template has to appear here — the
            // user can drop any of them into the text fields, and one that's missing renders as a
            // literal "%TOKEN%" in the preview even though the real email resolves it.
            "%CUSTOMER_DISPLAY_NAME%": "Sample Customer",
            "%CUSTOMER_FIRST_NAME%": "Sample",
            "%CUSTOMER_LAST_NAME%": "Customer",
            "%CUSTOMER_COMPANY_NAME%": "Sample Company",
            "%CUSTOMER_ADDRESS1%": "123 Main St",
            "%CUSTOMER_ADDRESS2%": "Suite 100",
            "%CUSTOMER_CITY%": "Anytown",
            "%CUSTOMER_STATE%": "ST",
            "%CUSTOMER_ZIP_CODE%": "00000",
            "%CUSTOMER_POSTAL_CODE%": "00000",
            "%CUSTOMER_COUNTRY%": "USA",
            "%CUSTOMER_FULL_ADDRESS%": "123 Main St, Anytown, ST 00000",
            "%CUSTOMER_HOME_PHONE%": "(555) 555-1234",
            "%CUSTOMER_WORK_PHONE%": "(555) 555-5678",
            "%CUSTOMER_CELL_PHONE%": "(555) 555-9012",
            "%CUSTOMER_EMAIL%": "customer@example.com",
            "%PAYMENT_DATE%": new Date().toLocaleDateString(),
            "%PAYMENT_TYPE%": "Credit Card",
            "%PAYMENT_METHOD%": "Visa 4242",
            "%PAYMENT_AUTH_LINE%": `<div style="font-size:11px; color:#${themeColor}; margin-top:3px;">Auth # 08391D</div>`,
            "%PAYMENT_REFERENCE_NUMBER%": "RCP-000000-001",
            "%PAYMENT_AMOUNT%": "$1,390.95",
        };
        for (const key of Object.keys(values)) {
            html = html.split(key).join(values[key]);
        }
        return html;
    }

    // Mirrors PaymentServices.BuildPaymentSummaryRowsHtml with one sample invoice row.
    private buildPaymentSummaryRowsHtml(themeColor: string): string {
        const tint = this.tintThemeColor(themeColor, .12);
        const header = ["Invoice #", "Invoice Date", "Purchase Order"]
            .map(x => `<td align="left" style="padding:12px 6px; font-size:12px; font-weight:bold; color:#333333;">${x}</td>`)
            .join("")
            + ["Amount", "Payment"]
                .map(x => `<td align="right" style="padding:12px 6px; font-size:12px; font-weight:bold; color:#333333;">${x}</td>`)
                .join("");

        const row =
            `<td align="left" valign="top" style="padding:10px 6px; font-size:12px; color:#333333;">736459</td>` +
            `<td align="left" valign="top" style="padding:10px 6px; font-size:12px; color:#333333;">${new Date().toLocaleDateString()}</td>` +
            `<td align="left" valign="top" style="padding:10px 6px; font-size:12px; color:#333333;">PO-SAMPLE</td>` +
            `<td align="right" valign="top" style="padding:10px 6px; font-size:12px; color:#333333;">$1,390.95</td>` +
            `<td align="right" valign="top" style="padding:10px 6px; font-size:12px; font-weight:bold; color:#1B8A3A;">$1,390.95</td>`;

        return `<table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" style="width:100%; font-family:Arial, Helvetica, sans-serif;">` +
            `<tr>${header}</tr>` +
            `<tr><td colspan="5" style="border-top:1px solid #e6e6ea; font-size:0; line-height:0;">&nbsp;</td></tr>` +
            `<tr>${row}</tr>` +
            `</table>` +
            `<table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" style="width:100%; background:#${tint}; margin-top:10px;"><tr>` +
            `<td align="right" style="padding:14px 12px; font-size:13px; font-weight:bold; color:#333333;">Total Paid</td>` +
            `<td align="right" width="120" style="width:120px; padding:14px 12px; font-size:14px; font-weight:bold; color:#333333;">$1,390.95</td>` +
            `</tr></table>`;
    }

    // Mirrors PaymentServices.BuildPaymentCustomNotesHtml.
    private buildPaymentCustomNotesHtml(model: IPaymentTemplateCustomizationModel): string {
        if (!model?.customNotes || !model.customNotes.trim())
            return "";

        const notes = this.escapeHtml(model.customNotes).replace(/\r\n|\n/g, "<br>");
        return `<tr><td style="padding:18px 24px 0 24px; font-family:Arial, Helvetica, sans-serif; font-size:12px; line-height:1.6; color:#333333;">${notes}</td></tr>`;
    }

    // Mirrors PaymentServices.BuildPaymentCtaHtml.
    private buildPaymentCtaHtml(model: IPaymentTemplateCustomizationModel, themeColor: string): string {
        const hasText = (model?.ctaHeading || "").trim() || (model?.ctaText || "").trim();
        if (!hasText)
            return "";

        const tint = this.tintThemeColor(themeColor, .12);

        let button = "";
        if ((model.ctaButtonText || "").trim() && (model.ctaButtonUrl || "").trim()) {
            button = `<a href="${this.escapeHtml(model.ctaButtonUrl)}" target="_blank" rel="noopener noreferrer" ` +
                `style="display:inline-block; border:1px solid #${themeColor}; color:#${themeColor}; font-family:Arial,sans-serif; ` +
                `font-size:13px; font-weight:bold; text-decoration:none; padding:12px 22px; border-radius:4px;">` +
                `${this.escapeHtml(model.ctaButtonText)}</a>`;
        }

        const buttonCell = button
            ? `<td valign="middle" align="right" style="padding:18px 18px 18px 0;">${button}</td>`
            : "";

        return `<tr><td style="padding:22px 24px 0 24px;">` +
            `<table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" style="width:100%; border:1px solid #e6e6ea;"><tr>` +
            `<td valign="middle" width="70" style="width:70px; padding:18px 0 18px 18px;">` +
            `<div style="width:52px; height:52px; background:#${tint}; border-radius:26px; text-align:center; font-size:0; line-height:0;">` +
            `<span style="display:inline-block; margin-top:12px;">${this.paymentIcon(themeColor, 28, EmailService.ICON_SHIELD)}</span></div></td>` +
            `<td valign="middle" style="padding:18px 14px; font-family:Arial, Helvetica, sans-serif;">` +
            `<div style="font-size:13px; font-weight:bold; color:#${themeColor};">${this.escapeHtml(model.ctaHeading)}</div>` +
            `<div style="font-size:12px; color:#333333; line-height:1.5; margin-top:4px;">${this.escapeHtml(model.ctaText).replace(/\r\n|\n/g, "<br>")}</div></td>` +
            buttonCell +
            `</tr></table></td></tr>`;
    }

    // Mirrors PaymentServices.PaymentIcon and its icon geometry. Inline SVG rather than emoji: the receipt
    // PDF renders this same markup and wkhtmltopdf has no emoji font, so every emoji printed as an empty
    // box on the customer's PDF. The width/height attributes give wkhtmltopdf's WebKit an intrinsic size;
    // without them it falls back to a 300x150 box and squeezes the drawing into half its width.
    private paymentIcon(themeColor: string, size: number, paths: string): string {
        return `<img src="data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='${size}' height='${size}' ` +
            `viewBox='0 0 24 24' fill='none' stroke='%23${themeColor}' stroke-width='1.7' stroke-linecap='round' stroke-linejoin='round'>${paths}</svg>" ` +
            `width="${size}" height="${size}" alt="" style="width:${size}px; height:${size}px; display:block;">`;
    }

    private static readonly ICON_SHIELD = "<path d='M12 3.2l7 2.8v5.4c0 4.2-2.9 7.9-7 9.4-4.1-1.5-7-5.2-7-9.4V6z'/><path d='M9 12l2 2 4-4'/>";
    private static readonly ICON_SPEECH_BUBBLE = "<path d='M20.5 11.7c0 4-3.8 7.2-8.5 7.2-1 0-2-.2-2.9-.4L4 21l1.3-3.6C4.2 16.2 3.5 14 3.5 11.7c0-4 3.8-7.2 8.5-7.2s8.5 3.2 8.5 7.2z'/>";
    private static readonly ICON_CAMERA = "<rect x='3' y='7.5' width='18' height='12.5' rx='2'/><path d='M8.2 7.5L9.6 4.8h4.8l1.4 2.7'/><circle cx='12' cy='13.8' r='3.6'/>";
    private static readonly ICON_GLOBE = "<circle cx='12' cy='12' r='8.5'/><path d='M3.5 12h17'/><path d='M12 3.5c2.3 2.4 3.5 5.3 3.5 8.5S14.3 18.1 12 20.5c-2.3-2.4-3.5-5.3-3.5-8.5S9.7 5.9 12 3.5z'/>";
    private static readonly ICON_PHONE = "<path d='M7 3.5H4.6A1.6 1.6 0 0 0 3 5.2 15.8 15.8 0 0 0 18.8 21a1.6 1.6 0 0 0 1.7-1.6V17l-4.2-1.6-1.9 1.9a12.6 12.6 0 0 1-5.6-5.6l1.9-1.9z'/>";

    // Mirrors PaymentServices.BuildPaymentFooterLinksHtml.
    private buildPaymentFooterLinksHtml(model: IPaymentTemplateCustomizationModel, themeColor: string): string {
        const company = GlobalsService.company;
        const links: { icon: string, label: string, subText: string, url: string }[] = [];

        if ((model?.reviewUrl || "").trim())
            links.push({ icon: this.paymentIcon(themeColor, 20, EmailService.ICON_SPEECH_BUBBLE), label: "Review", subText: model.reviewSubText, url: model.reviewUrl });

        if ((model?.followUsUrl || "").trim())
            links.push({ icon: this.paymentIcon(themeColor, 20, EmailService.ICON_CAMERA), label: "Follow us", subText: model.followUsSubText, url: model.followUsUrl });

        if ((model?.websiteUrl || "").trim())
            links.push({ icon: this.paymentIcon(themeColor, 20, EmailService.ICON_GLOBE), label: "Visit our website", subText: model.websiteSubText, url: model.websiteUrl });

        if (model?.showContact) {
            const contactSubText = (model.contactSubText || "").trim()
                ? model.contactSubText
                : [company?.phoneNumber, company?.email].filter(x => x && x.trim()).join("\n");

            links.push({ icon: this.paymentIcon(themeColor, 20, EmailService.ICON_PHONE), label: "Contact us", subText: contactSubText, url: null });
        }

        if (links.length === 0)
            return "";

        const cellWidth = Number((100 / links.length).toFixed(2));

        let cells = "";
        links.forEach((link, i) => {
            let inner =
                `<table role="presentation" cellpadding="0" cellspacing="0" border="0"><tr>` +
                `<td valign="top" width="20" style="width:20px; padding-top:1px; font-size:0; line-height:0;">${link.icon}</td>` +
                `<td valign="top" style="padding-left:8px; font-family:Arial, Helvetica, sans-serif;">` +
                `<div style="font-size:12px; font-weight:bold; color:#${themeColor};">${this.escapeHtml(link.label)}</div>` +
                `<div style="font-size:10px; color:#666666; line-height:1.4; margin-top:2px;">${this.escapeHtml(link.subText).replace(/\r\n|\n/g, "<br>")}</div>` +
                `</td></tr></table>`;

            if ((link.url || "").trim())
                inner = `<a href="${this.escapeHtml(link.url)}" target="_blank" rel="noopener noreferrer" style="text-decoration:none;">${inner}</a>`;

            const borderStyle = (i === 0) ? "" : "border-left:1px solid #e6e6ea;";
            cells += `<td valign="top" width="${cellWidth}%" style="width:${cellWidth}%; padding:0 10px; ${borderStyle}">${inner}</td>`;
        });

        return `<tr><td style="padding:22px 24px 18px 24px;">` +
            `<table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" style="width:100%;"><tr>${cells}</tr></table>` +
            `</td></tr>`;
    }

    private escapeHtml(value: string): string {
        return (value || "")
            .replace(/&/g, "&amp;")
            .replace(/</g, "&lt;")
            .replace(/>/g, "&gt;")
            .replace(/"/g, "&quot;");
    }

    // Final Service (invoice / sales order) email layout — kept in sync with
    // Services/EmailTemplatesRawHTML.cs BaseInvoiceHTML.
    getBaseInvoiceEmailRawHTML() {
        return `<!doctype html>
<html lang="und" dir="auto">
<head>
  <meta http-equiv="Content-Type" content="text/html; charset=UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1">
  <title></title>
</head>
<body style="margin:0; padding:0; font-family:Arial, sans-serif; background-color:transparent; color:#333333;">

<!-- Page shell: one centred 600px column that every section sits in, the same shell the other four
     layouts use. The sections used to centre themselves one by one with margin:0 auto, which Outlook
     and some webmail ignore, while the header centred through align=center: the header landed in the
     middle of the window and everything under it hugged the left edge. text-align:left undoes the
     centring align=center pushes down into the content. The font is repeated here because webmail
     drops the body tag and its style along with it. -->
<table role="presentation" width="100%" cellspacing="0" cellpadding="0" border="0">
  <tr>
    <td align="center">
      <table role="presentation" width="600" cellspacing="0" cellpadding="0" border="0" style="width:600px; max-width:600px; font-family:Arial, sans-serif; color:#333333;">
        <tr>
          <td style="text-align:left;">

<!-- Header wrapper -->
<table role="presentation" width="100%" cellspacing="0" cellpadding="0" border="0">
  <tr>
    <td align="center">

      <!-- Inner header table (fixed width, same as items) -->
      <table role="presentation"
             width="600"
             cellspacing="0"
             cellpadding="0"
             border="0"
             style="background-color:#%HEADER_BG%;max-width:600px;overflow-x:hidden;">
        <tr>
          <td style="padding:26px 10px 26px 24px; vertical-align:middle; width:40%;">
            <!-- %HEADER_IMAGE%, not %COMPANY_LOGO%: this is the one slot the template's Header Image fills.
                 The band is painted in the accent color, so that image is often a white-on-transparent
                 logo, and the same file on the white footer below (or the plain invoice layout) vanishes.
                 Those slots keep %COMPANY_LOGO% and always get the company's default logo. -->
            <img src="%HEADER_IMAGE%" width="150" alt="Logo" style="max-width:150px; height:auto; display:block;">
          </td>
          <td style="padding:26px 24px 26px 24px; vertical-align:middle; text-align:left; color:#%HEADER_TEXT%;">
            <div style="font-size:26px; font-weight:bold; line-height:1.2; color:#%HEADER_TEXT%;">%SERVICE_HEADER_TITLE%</div>
            <div style="font-size:13px; margin-top:8px; color:#%HEADER_TEXT%;">%SERVICE_HEADER_SUBTITLE%</div>
            <!-- No white-space:nowrap here. A table can't shrink below its min-content width, so nowrap
                 text longer than 600px stretches this whole band wider than every other section (and
                 the text-overflow ellipsis never fires, because nothing bounds the width). Wrap instead.
                 max-height caps the banner at roughly 5in / 480px. overflow:hidden is required - max-height
                 alone bounds the box but lets the text spill out of it. -->
            <div style="font-size:14px; margin-top:10px; color:#%HEADER_TEXT%; max-height:400px; overflow:hidden;">
              %CUSTOM_HEADER_TEXT%
            </div>
          </td>
        </tr>
      </table>

    </td>
  </tr>
</table>



  <!-- Technician Info -->
  <table role="presentation" width="100%" cellspacing="0" cellpadding="0" border="0">
    <tr>
      <td style="padding:20px;">
        <table role="presentation" width="100%" cellspacing="0" cellpadding="0" border="0">
          <tr>
            <!-- The photo's gap lives on its own cell, so GenerateInvoiceEmail can drop the whole cell when
                 the technician has no photo and the details line up with the sections below. Keep the
                 cell holding nothing but the img, or that match fails and an empty column is left behind. -->
            <td valign="top" style="width:70px; padding-right:15px;">
              <img src="%TECHNICIAN_IMAGE_URL%" width="70" alt="Tech" style="border-radius:50%; display:block;">
            </td>
            <td valign="top" style="font-size:14px;">
              <div><strong>Date Completed:</strong> %DATE_COMPLETED%</div>
              <div><strong>Serviced By:</strong> %TECHNICIAN_NAME%</div>
              <!-- Same reason as the header: nowrap notes drag this table past 600px and knock the whole
                   block out of line with the bands above and below it. -->
              <div><strong>Technician Notes:</strong> %TECHNICIAN_NOTES%</div>
            </td>
          </tr>
        </table>
      </td>
    </tr>
  </table>

  <!-- Service Location -->
  <!-- The Invoice/Quote heading rides in this section on purpose. GenerateInvoiceEmail strips everything
       between the Technician Info marker and the Service Location marker above when there is no completed
       appointment, so the heading has to sit AFTER that marker or it would be stripped along with the
       technician block. Do not name those markers literally in a comment - a nested opener ends this
       comment early and the rest leaks into the email as visible text. -->
  <table role="presentation" width="100%" cellspacing="0" cellpadding="0" border="0" style="max-width:600px; margin:0 auto; background:#ffffff;">
    <tr>
      <td style="padding:6px 20px 12px 20px;">
        <table role="presentation" cellspacing="0" cellpadding="0" border="0">
          <tr>
            <td valign="middle" style="font-size:18px; color:#%THEME_COLOR%;">&#129534;</td>
            <td valign="middle" style="padding-left:8px; font-size:15px; font-weight:bold; color:#%THEME_COLOR%;">%QUOTE_OR_INVOICE_TEXT%</td>
          </tr>
        </table>
      </td>
    </tr>
  </table>

  <table role="presentation" width="100%" cellspacing="0" cellpadding="0" border="0" style="max-width:600px; margin:0 auto; background:#ffffff;">
    <tr>
      <td style="padding:0 20px;">
        <table role="presentation" width="100%" cellspacing="0" cellpadding="0" border="0" style="background:#%THEME_COLOR%; color:#ffffff;">
          <tr>
            <td style="text-align:left; padding:20px; font-size:13px; line-height:1.5; color:#ffffff;">
              <strong style="font-size:15px;">%CUSTOMER_FULL_NAME%</strong><br>
              %CUSTOMER_ADDRESS1%<br>
              %CUSTOMER_CITY%, %CUSTOMER_STATE%, %CUSTOMER_ZIP_CODE%
            </td>
            <td style="text-align:right; padding:20px; font-size:13px; line-height:1.5; color:#ffffff;">
              <strong style="font-size:26px;">$%BALANCE_DUE%</strong><br>
              Terms: %CUSTOMER_PAYMENT_TERMS%<br>
              Due: %INVOICE_DUE_DATE%
            </td>
          </tr>
        </table>
      </td>
    </tr>
  </table>

  <!-- Invoice Items -->
  <table role="presentation" width="100%" cellspacing="0" cellpadding="0" border="0" style="max-width:600px; margin:0 auto; background:#ffffff; padding:20px;">
    <tr>
      <td>
        <!-- Inner table to match layout -->
        <table role="presentation" width="100%" cellspacing="0" cellpadding="5" border="0" style="border-collapse:collapse;">
          %LINE_ITEMS%
        </table>
        <div style="font-family:Arial, sans-serif;font-size:14px;line-height:1.5;text-align:left;color:#333333;white-space:pre-wrap;padding-top:10px;">%CUSTOM_NOTES%</div>
      </td>
    </tr>
  </table>

  <!-- Buttons -->
  <table role="presentation" width="100%" cellspacing="0" cellpadding="0" border="0" style="max-width:600px; margin:10px auto; text-align:center;">
    <tr>
      <td>
        %ACCEPT_BUTTON% %PAY_BUTTON%
      </td>
    </tr>
  </table>

  <!-- Photo Links -->
  <table role="presentation" width="100%" cellspacing="0" cellpadding="0" border="0" style="max-width:600px; margin:0 auto; background:#%THEME_COLOR%; padding:25px; color:#ffffff;">
    <tr>
      <td style="text-align:center; font-weight:bold;">Photos from the Job</td>
    </tr>
    <tr>
      <td style="text-align:center;">%PHOTO_IMAGES%</td>
    </tr>
  </table>

  <!-- Orenda Section -->
  <table role="presentation" width="100%" cellspacing="0" cellpadding="0" border="0" style="max-width:600px; margin:0 auto; background:#ffffff; padding:20px;">
    <tr>
      <td>%ORENDA_HISTORY%</td>
    </tr>
  </table>

  <!-- Footer -->
  <table role="presentation" width="600" cellspacing="0" cellpadding="0" border="0" style="width:100%; max-width:600px; margin:0 auto; background:#ffffff; text-align:center; font-size:13px; line-height:1.5; color:#666;">
    <tr>
      <td align="center" style="padding:20px 20px 0 20px;">
        <!-- Logo and company info are two cells of ONE row, not two rows - separate rows can only stack
             the logo above the text. The nested table is what centres the pair as a group; the outer
             cell is 600px wide, so laying them out directly in it would strand the text far to the right. -->
        <table role="presentation" cellspacing="0" cellpadding="0" border="0" align="center">
          <tr>
            <td valign="middle" style="padding-right:15px;">
              <img src="%COMPANY_LOGO%" width="80" alt="Company Logo" style="display:block;">
            </td>
            <td valign="middle" align="left" style="font-size:13px; line-height:1.5; color:#666; text-align:left;">
              <strong>%COMPANY_NAME%</strong><br>
              %COMPANY_FULL_ADDRESS%<br>
              %COMPANY_PHONE_NUMBER%<br>
              %COMPANY_WEBSITE%
            </td>
          </tr>
        </table>
      </td>
    </tr>
    <tr>
      <td align="center" style="padding:12px 20px 0 20px;">
        <div>Have a great day!</div>
        %REVIEW_BUTTON%
      </td>
    </tr>
    <tr>
      <td align="center" style="padding:0 20px 20px 20px;">
        <a href="https://www.skedit.com" target="_blank" style="display:block; text-align:center;">
          <img src="https://docs.skedit.com/powered-by-skedit.png"
               width="90"
               alt="Powered by Skedit"
               style="display:block; margin:10px auto 0 auto;">
        </a>
      </td>
    </tr>
  </table>

          </td>
        </tr>
      </table>
    </td>
  </tr>
</table>

</body>
</html>`;
    }

}
